/**
 * House Vault — where the wifi password and the alarm code live.
 *
 * Adults only (enforced in the Worker, not here). Design intent: calm and
 * trustworthy. Secrets are dots by default; revealing one is a deliberate,
 * one-item-at-a-time act that the UI narrates honestly.
 *
 * Reveal lifecycle, all handled here so "hide everything" is always one call:
 *   * A reveal fetches that one item's plaintext from /vault/:id/reveal.
 *   * It auto-masks after REVEAL_SECONDS, with a visible countdown.
 *   * Leaving the page, hiding the tab, or unmounting masks everything and
 *     best-effort clears the clipboard if the last thing copied was a secret.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiRequestError } from '../../lib/api';
import type { VaultConfig, VaultItem, VaultSecret } from '../../shared/types';
import VaultItemCard from './VaultItemCard';
import VaultItemDialog from './VaultItemDialog';

export type CopyKind = 'secret' | 'username';
export interface CopyState {
  id: number;
  kind: CopyKind;
}

/** A revealed secret masks itself again after this long, no interaction needed. */
const REVEAL_SECONDS = 45;
/** How long a copied secret is allowed to sit in the clipboard before we wipe it. */
const CLIPBOARD_CLEAR_MS = 60_000;
const UNCATEGORIZED = 'Uncategorized';

/**
 * Categories are free text, so "Utility accounts" would produce an id with a
 * space in it — and aria-labelledby is a space-separated LIST of ids, so the
 * section would resolve to two nonexistent ids and end up with no accessible
 * name at all. Slugify, and keep a stable fallback for a category that slugs to
 * nothing (e.g. "🔑").
 */
function groupId(category: string, index: number): string {
  const slug = category
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  // The index keeps it unique even if two categories slug to the same string
  // ("Utility accounts" / "utility-accounts") or to nothing at all ("🔑").
  return `vault-group-${slug || 'other'}-${index}`;
}

type DialogState = { mode: 'add' } | { mode: 'edit'; item: VaultItem } | null;

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiRequestError) return err.body.detail ?? err.body.error ?? fallback;
  return fallback;
}

export default function VaultPage() {
  const [items, setItems] = useState<VaultItem[] | null>(null);
  const [config, setConfig] = useState<VaultConfig | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [query, setQuery] = useState('');
  const [dialog, setDialog] = useState<DialogState>(null);

  const [revealed, setRevealed] = useState<Record<number, string>>({});
  const [revealedAt, setRevealedAt] = useState<Record<number, number>>({});
  const [revealing, setRevealing] = useState<number | null>(null);
  const [copied, setCopied] = useState<CopyState | null>(null);
  const [now, setNow] = useState(() => Date.now());

  /** True while a *secret* is sitting in the OS clipboard because of us. */
  const clipboardDirty = useRef(false);
  const clipboardTimer = useRef<number | null>(null);
  const copiedTimer = useRef<number | null>(null);

  const clearClipboard = useCallback(() => {
    if (!clipboardDirty.current) return;
    clipboardDirty.current = false;
    // Best effort: some browsers refuse a clipboard write without a user gesture.
    // Failing to wipe it is not an error we can do anything about, so stay quiet.
    void navigator.clipboard?.writeText('').catch(() => {});
  }, []);

  /** Re-mask everything and drop every plaintext we are holding in memory. */
  const maskAll = useCallback(() => {
    setRevealed({});
    setRevealedAt({});
    setCopied(null);
    if (clipboardTimer.current) window.clearTimeout(clipboardTimer.current);
    if (copiedTimer.current) window.clearTimeout(copiedTimer.current);
    clearClipboard();
  }, [clearClipboard]);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [rows, cfg] = await Promise.all([
        api.get<VaultItem[]>('/vault'),
        api.get<VaultConfig>('/vault/config'),
      ]);
      setItems(rows);
      setConfig(cfg);
    } catch (err) {
      setLoadError(errorMessage(err, "The vault couldn't be loaded."));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Navigate away, background the tab, lock the phone → everything re-masks.
  useEffect(() => {
    const onHidden = () => {
      if (document.visibilityState === 'hidden') maskAll();
    };
    document.addEventListener('visibilitychange', onHidden);
    window.addEventListener('pagehide', maskAll);
    return () => {
      document.removeEventListener('visibilitychange', onHidden);
      window.removeEventListener('pagehide', maskAll);
      maskAll(); // unmount = leaving the vault
    };
  }, [maskAll]);

  // One ticker, only while something is actually revealed: drives the countdown
  // and the auto-mask.
  const anyRevealed = Object.keys(revealed).length > 0;
  useEffect(() => {
    if (!anyRevealed) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [anyRevealed]);

  useEffect(() => {
    const expired = Object.entries(revealedAt)
      .filter(([, at]) => now - at >= REVEAL_SECONDS * 1000)
      .map(([id]) => Number(id));
    if (!expired.length) return;

    setRevealed((prev) => {
      const next = { ...prev };
      for (const id of expired) delete next[id];
      return next;
    });
    setRevealedAt((prev) => {
      const next = { ...prev };
      for (const id of expired) delete next[id];
      return next;
    });
    setCopied((prev) => (prev && prev.kind === 'secret' && expired.includes(prev.id) ? null : prev));
  }, [now, revealedAt]);

  const handleReveal = useCallback(async (id: number) => {
    setActionError(null);
    setRevealing(id);
    try {
      const res = await api.get<VaultSecret>(`/vault/${id}/reveal`);
      const at = Date.now();
      setRevealed((prev) => ({ ...prev, [id]: res.secret }));
      setRevealedAt((prev) => ({ ...prev, [id]: at }));
      // The 1s ticker only runs while something is revealed, so `now` is as stale
      // as the gap since the last reveal. Without this the countdown's first frame
      // is computed from an old `now` and shows a nonsense number ("Hides in 165s")
      // for up to a second before snapping to the real one.
      setNow(at);
    } catch (err) {
      setActionError(errorMessage(err, "That secret couldn't be revealed."));
    } finally {
      setRevealing(null);
    }
  }, []);

  const handleHide = useCallback(
    (id: number) => {
      setRevealed((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setRevealedAt((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setCopied((prev) => (prev?.id === id ? null : prev));
      clearClipboard();
    },
    [clearClipboard]
  );

  const handleCopy = useCallback(
    async (id: number, kind: CopyKind, value: string) => {
      setActionError(null);
      try {
        await navigator.clipboard.writeText(value);
      } catch {
        setActionError(
          "Your browser wouldn't let the app use the clipboard. You can select the value and copy it by hand."
        );
        return;
      }

      setCopied({ id, kind });
      if (copiedTimer.current) window.clearTimeout(copiedTimer.current);
      copiedTimer.current = window.setTimeout(() => setCopied(null), 4000);

      if (kind === 'secret') {
        clipboardDirty.current = true;
        if (clipboardTimer.current) window.clearTimeout(clipboardTimer.current);
        clipboardTimer.current = window.setTimeout(clearClipboard, CLIPBOARD_CLEAR_MS);
      }
    },
    [clearClipboard]
  );

  const handleSaved = useCallback(
    (saved: VaultItem) => {
      // A saved secret invalidates whatever plaintext we were showing for it.
      handleHide(saved.id);
      setItems((prev) => {
        const rest = (prev ?? []).filter((i) => i.id !== saved.id);
        return [...rest, saved].sort(
          (a, b) =>
            (a.category ?? '￿').localeCompare(b.category ?? '￿', undefined, {
              sensitivity: 'base',
            }) || a.title.localeCompare(b.title, undefined, { sensitivity: 'base' })
        );
      });
      setDialog(null);
    },
    [handleHide]
  );

  const handleDelete = useCallback(
    async (item: VaultItem) => {
      setActionError(null);
      try {
        await api.del<VaultItem>(`/vault/${item.id}`);
        handleHide(item.id);
        setItems((prev) => (prev ?? []).filter((i) => i.id !== item.id));
      } catch (err) {
        setActionError(errorMessage(err, `"${item.title}" couldn't be deleted.`));
      }
    },
    [handleHide]
  );

  const knownCategories = useMemo(() => {
    const set = new Set<string>();
    for (const item of items ?? []) if (item.category) set.add(item.category);
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [items]);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matched = (items ?? []).filter((item) => {
      if (!q) return true;
      return [item.title, item.category, item.username, item.url, item.notes]
        .filter((v): v is string => !!v)
        .some((v) => v.toLowerCase().includes(q));
    });

    const byCategory = new Map<string, VaultItem[]>();
    for (const item of matched) {
      const key = item.category?.trim() || UNCATEGORIZED;
      const bucket = byCategory.get(key);
      if (bucket) bucket.push(item);
      else byCategory.set(key, [item]);
    }

    return [...byCategory.entries()]
      .sort(([a], [b]) => {
        if (a === UNCATEGORIZED) return 1; // no-category items sink to the bottom
        if (b === UNCATEGORIZED) return -1;
        return a.localeCompare(b, undefined, { sensitivity: 'base' });
      })
      .map(([category, list]) => ({
        category,
        items: list.sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' })),
      }));
  }, [items, query]);

  const matchCount = groups.reduce((sum, g) => sum + g.items.length, 0);
  const encryptionReady = config?.encryption_ready ?? true;

  return (
    <div className="pb-16">
      <Header
        onAdd={() => setDialog({ mode: 'add' })}
        itemCount={items?.length ?? 0}
        canAdd={items !== null}
      />

      {config && !config.encryption_ready && (
        <ConfigWarning reason={config.reason ?? 'The vault encryption key is not configured.'} />
      )}

      {actionError && (
        <Banner tone="error" onDismiss={() => setActionError(null)}>
          {actionError}
        </Banner>
      )}

      {/* Screen-reader narration of reveal/copy, which are otherwise silent. */}
      <p className="sr-only" role="status" aria-live="polite">
        {copied
          ? copied.kind === 'secret'
            ? 'Secret copied to the clipboard. It will be cleared in one minute.'
            : 'Username copied to the clipboard.'
          : anyRevealed
            ? 'A secret is showing. It will hide itself shortly.'
            : ''}
      </p>

      {items === null && !loadError && <LoadingState />}
      {loadError && <ErrorState message={loadError} onRetry={() => void load()} />}

      {items !== null && items.length === 0 && (
        <EmptyState onAdd={() => setDialog({ mode: 'add' })} />
      )}

      {items !== null && items.length > 0 && (
        <>
          <SearchBar value={query} onChange={setQuery} resultCount={matchCount} />

          {matchCount === 0 ? (
            <NoMatchState query={query} onClear={() => setQuery('')} />
          ) : (
            <div className="mt-6 space-y-8">
              {groups.map((group, i) => (
                <section key={group.category} aria-labelledby={groupId(group.category, i)}>
                  <h2
                    id={groupId(group.category, i)}
                    className="mb-3 flex items-baseline gap-2 font-display text-sm font-semibold tracking-wide text-ink-soft uppercase"
                  >
                    {group.category}
                    <span className="text-xs font-normal normal-case text-ink-faint">
                      {group.items.length} {group.items.length === 1 ? 'item' : 'items'}
                    </span>
                  </h2>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {group.items.map((item) => (
                      <VaultItemCard
                        key={item.id}
                        item={item}
                        secret={revealed[item.id] ?? null}
                        revealing={revealing === item.id}
                        secondsLeft={
                          revealedAt[item.id]
                            ? Math.max(
                                0,
                                REVEAL_SECONDS - Math.floor((now - revealedAt[item.id]) / 1000)
                              )
                            : null
                        }
                        copied={copied?.id === item.id ? copied.kind : null}
                        encryptionReady={encryptionReady}
                        onReveal={() => void handleReveal(item.id)}
                        onHide={() => handleHide(item.id)}
                        onCopy={(kind, value) => void handleCopy(item.id, kind, value)}
                        onEdit={() => setDialog({ mode: 'edit', item })}
                        onDelete={() => void handleDelete(item)}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </>
      )}

      {dialog && (
        <VaultItemDialog
          item={dialog.mode === 'edit' ? dialog.item : null}
          categories={knownCategories}
          encryptionReady={encryptionReady}
          onSaved={handleSaved}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------- chrome

function Header({
  onAdd,
  itemCount,
  canAdd,
}: {
  onAdd: () => void;
  itemCount: number;
  canAdd: boolean;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="flex items-center gap-2 font-display text-2xl font-semibold text-ink">
          <LockIcon className="h-5 w-5 text-sage" />
          House Vault
        </h1>
        <p className="mt-1 max-w-prose text-sm text-ink-soft">
          Wi-Fi passwords, alarm codes, utility accounts. Encrypted before it's stored, shown one
          item at a time, only when you ask.
          {itemCount > 0 && (
            <span className="text-ink-faint">
              {' '}
              {itemCount} {itemCount === 1 ? 'item' : 'items'} saved.
            </span>
          )}
        </p>
      </div>
      {canAdd && (
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex h-11 items-center gap-1.5 rounded-full bg-sage px-5 text-sm font-medium text-white transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2 focus-visible:ring-offset-canvas focus-visible:outline-none"
        >
          <PlusIcon className="h-4 w-4" />
          Add item
        </button>
      )}
    </div>
  );
}

function SearchBar({
  value,
  onChange,
  resultCount,
}: {
  value: string;
  onChange: (v: string) => void;
  resultCount: number;
}) {
  return (
    <div className="relative">
      <label htmlFor="vault-search" className="sr-only">
        Search the vault by title, category, username, or notes
      </label>
      <SearchIcon className="pointer-events-none absolute top-1/2 left-4 h-4 w-4 -translate-y-1/2 text-ink-faint" />
      <input
        id="vault-search"
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search titles, categories, usernames…"
        className="h-11 w-full rounded-full border border-border bg-surface pr-4 pl-11 text-sm text-ink placeholder:text-ink-faint focus-visible:border-sage focus-visible:ring-2 focus-visible:ring-sage focus-visible:outline-none"
      />
      <p className="sr-only" role="status" aria-live="polite">
        {value ? `${resultCount} matching ${resultCount === 1 ? 'item' : 'items'}` : ''}
      </p>
    </div>
  );
}

function ConfigWarning({ reason }: { reason: string }) {
  return (
    <div
      role="alert"
      className="mb-6 flex items-start gap-3 rounded-card border border-honey/40 bg-honey-soft p-4"
    >
      <WarnIcon className="mt-0.5 h-5 w-5 shrink-0 text-honey" />
      <div className="text-sm">
        <p className="font-medium text-ink">Vault encryption isn't set up yet</p>
        <p className="mt-1 text-ink-soft">{reason}</p>
        <p className="mt-2 text-ink-soft">
          Until then you can still save titles, usernames and notes — but secrets can't be saved or
          revealed. Nothing is ever stored unencrypted.
        </p>
      </div>
    </div>
  );
}

function Banner({
  tone,
  children,
  onDismiss,
}: {
  tone: 'error';
  children: React.ReactNode;
  onDismiss: () => void;
}) {
  return (
    <div
      role="alert"
      className={`mb-6 flex items-start gap-3 rounded-card border p-4 text-sm ${
        tone === 'error' ? 'border-clay/40 bg-clay-soft text-ink' : ''
      }`}
    >
      <WarnIcon className="mt-0.5 h-5 w-5 shrink-0 text-clay" />
      <p className="flex-1">{children}</p>
      <button
        type="button"
        onClick={onDismiss}
        className="-m-2 rounded-full p-2 text-ink-soft hover:text-ink focus-visible:ring-2 focus-visible:ring-clay focus-visible:outline-none"
        aria-label="Dismiss"
      >
        <XIcon className="h-4 w-4" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------- states

function LoadingState() {
  return (
    <div className="mt-6 grid gap-3 sm:grid-cols-2" aria-busy="true" aria-label="Loading the vault">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="animate-pulse rounded-card border border-border bg-surface p-4">
          <div className="h-4 w-1/3 rounded bg-surface-sunk" />
          <div className="mt-3 h-3 w-1/2 rounded bg-surface-sunk" />
          <div className="mt-4 h-9 w-full rounded bg-surface-sunk" />
        </div>
      ))}
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div
      role="alert"
      className="rounded-card border border-clay/40 bg-clay-soft p-6 text-center text-sm"
    >
      <p className="font-medium text-ink">{message}</p>
      <p className="mt-1 text-ink-soft">Nothing was changed. It's safe to try again.</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-4 inline-flex h-11 items-center rounded-full bg-clay px-5 font-medium text-white transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-clay focus-visible:ring-offset-2 focus-visible:ring-offset-canvas focus-visible:outline-none"
      >
        Try again
      </button>
    </div>
  );
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="rounded-card border border-border bg-surface px-6 py-12 text-center">
      <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-sage-soft">
        <LockIcon className="h-6 w-6 text-sage" />
      </div>
      <h2 className="mt-4 font-display text-lg font-semibold text-ink">The vault is empty</h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-ink-soft">
        This is the one place the household's details live: the Wi-Fi password, the alarm code, the
        garage keypad, the water company login, the HVAC guy's number. Secrets are encrypted before
        they're stored and only ever shown one at a time.
      </p>
      <button
        type="button"
        onClick={onAdd}
        className="mt-6 inline-flex h-11 items-center gap-1.5 rounded-full bg-sage px-6 text-sm font-medium text-white transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2 focus-visible:ring-offset-canvas focus-visible:outline-none"
      >
        <PlusIcon className="h-4 w-4" />
        Add the first item
      </button>
      <p className="mt-3 text-xs text-ink-faint">Start with the Wi-Fi password — you'll use it most.</p>
    </div>
  );
}

function NoMatchState({ query, onClear }: { query: string; onClear: () => void }) {
  return (
    <div className="mt-6 rounded-card border border-border bg-surface px-6 py-10 text-center">
      <p className="text-sm text-ink">
        Nothing in the vault matches <span className="font-medium">“{query}”</span>.
      </p>
      <button
        type="button"
        onClick={onClear}
        className="mt-4 inline-flex h-11 items-center rounded-full border border-border bg-surface px-5 text-sm font-medium text-ink transition-colors hover:bg-surface-sunk focus-visible:ring-2 focus-visible:ring-sage focus-visible:outline-none"
      >
        Clear search
      </button>
    </div>
  );
}

// ---------------------------------------------------------------- icons

function LockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <rect x="4" y="10" width="16" height="10" rx="2" />
      <path d="M8 10V7a4 4 0 1 1 8 0v3" strokeLinecap="round" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M12 5v14M5 12h14" strokeLinecap="round" />
    </svg>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" strokeLinecap="round" />
    </svg>
  );
}

function WarnIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M12 4.5 2.8 20h18.4L12 4.5Z" strokeLinejoin="round" />
      <path d="M12 10v4M12 17.2v.1" strokeLinecap="round" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M6 6l12 12M18 6 6 18" strokeLinecap="round" />
    </svg>
  );
}
